import { type MaybeRef, type Ref, computed, unref } from "vue";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import { ONE_MINUTE_MS } from "@/helpers/constants";
import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import i18n from "@/i18n";
import permissionsPresetService from "@/services/modules/permissionsPreset";

import type {
  PermissionsPreset,
  PermissionsPresetFilter,
} from "@/models/permissionsPreset";

export const usePermissionsPresetsQuery = (
  params?: Ref<Partial<PermissionsPresetFilter>>
) => {
  const queryKey = computed(() =>
    QUERY_KEYS.PERMISSIONS_PRESETS.list(params?.value)
  );

  return useQuery({
    queryKey,
    queryFn: () => permissionsPresetService.getAll(params?.value),
  });
};

export const usePermissionsPresetQuery = (id: MaybeRef<string>) => {
  const queryKey = computed(() =>
    QUERY_KEYS.PERMISSIONS_PRESETS.detail(unref(id))
  );

  const enabled = computed(() => !!unref(id));

  return useQuery({
    queryKey,
    queryFn: () => permissionsPresetService.getSingle(unref(id)),
    enabled,
    staleTime: 10 * ONE_MINUTE_MS,
  });
};

export const useCreatePermissionPresetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<PermissionsPreset>) =>
      permissionsPresetService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PERMISSIONS_PRESETS.lists(),
      });
    },
  });
};

export const useUpdatePermissionPresetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<PermissionsPreset>) => {
      if (!payload.id) {
        // This should not happen in practice, but as a safeguard
        throw new Error("Preset ID is required for update.");
      }
      return permissionsPresetService.update(payload, payload.id);
    },
    onSuccess: (
      data: PermissionsPreset,
      variables: Partial<PermissionsPreset>
    ) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PERMISSIONS_PRESETS.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PERMISSIONS_PRESETS.detail(variables.id!),
      });
    },
  });
};

export const useDeletePermissionPresetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => permissionsPresetService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PERMISSIONS_PRESETS.lists(),
      });
    },
  });
};

export const useDuplicatePermissionPresetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<PermissionsPreset>) => {
      const duplicatedPayload = {
        ...payload,
        name: `${payload.name} ${i18n.global.t("COMMON.APPEND_COPY_TEXT")}`,
      };
      return permissionsPresetService.create(duplicatedPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PERMISSIONS_PRESETS.lists(),
      });
    },
  });
};
